<template>
  <div class="history-tab-container">
    <div class="history-tab">
      <div class="content-section">
        <div class="header">
          <div class="title-section">
            <h2>{{ $t("Apps.history") }}</h2>
          </div>
          <div class="filter-controls">
            <MultiSelectFilterComponent
              :available-filters="actionFilters"
              :initial-selected="historyId ? [] : selectedFilters"
              :placeholder-text="$t('Apps.allActions')"
              :default-selected="defaultActions"
              @update:selected-filters="handleFilterChange"
            />
          </div>
        </div>

        <transition-group name="timeline" tag="div" class="timeline">
          <div
            v-for="(change, index) in processedChanges"
            :key="change.timestamp"
            class="timeline-item"
            :ref="'timelineItem' + index"
          >
            <div class="timeline-date">
              <div class="date">{{ formatDate(change.timestamp) }}</div>
            </div>

            <div
              class="timeline-content"
              :class="{ expanded: expandedItems.includes(index) }"
            >
              <div class="change-header" @click="toggleItem(index)">
                <div class="change-summary-header">
                  <div class="change-type">
                    {{ processedChanges.length - index }}
                  </div>
                  <div class="change-summary">
                    {{ $t("Apps.changed") }} {{ change.changedFields.length }}
                    {{ $t("Apps.field") }}
                  </div>
                  <div class="action-tags">
                    <span
                      v-for="field in getLimitedFields(change.changedFields)"
                      :key="field.name"
                      class="action-tag"
                      :class="getFieldIconClass(field.name)"
                    >
                      {{ getFieldName(field.actionId) }}
                    </span>
                    <span
                      v-if="change.changedFields.length > maxVisibleTags"
                      class="action-more"
                      :title="
                        getMoreFieldsTooltip(
                          change.changedFields.slice(maxVisibleTags)
                        )
                      "
                      >...</span
                    >
                  </div>
                </div>
                <div class="history-actions">
                  <span
                    class="material-symbols-outlined"
                    @click.stop="toggleHistoricalView(change)"
                  >
                    visibility
                  </span>
                  <span
                    class="expand-arrow"
                    :class="{ expanded: expandedItems.includes(index) }"
                  >
                    <span class="material-symbols-outlined">expand_more</span>
                  </span>
                </div>
              </div>

              <transition
                name="expand"
                @enter="startTransition"
                @leave="endTransition"
              >
                <div
                  v-show="expandedItems.includes(index)"
                  class="changes-content"
                >
                  <div class="changes-grid">
                    <div
                      v-for="field in change.changedFields"
                      :key="field.name"
                      class="change-field"
                    >
                      <div class="field-header">
                        <span class="field-name">{{
                          formatFieldName(field.name)
                        }}</span>
                        <span
                          class="field-icon"
                          :class="getFieldIconClass(field.name)"
                        >
                          <span
                            class="material-symbols-outlined"
                            @click="
                              field.name === 'versions'
                                ? toggleVersionsModal(field.newValue)
                                : null
                            "
                            >{{ getFieldIcon(field.name) }}</span
                          >
                        </span>
                      </div>
                      <template v-if="isScreenshotAction(field.actionId)">
                        <div class="screenshots-section">
                          <div class="screenshots-comparison">
                            <div class="version-block">
                              <div class="version-label">
                                {{ $t("Apps.perviousVersion") }}
                              </div>
                              <div class="screenshots-scroll">
                                <div
                                  v-for="(screenshot, idx) in Object.values(
                                    field.oldValue
                                  )"
                                  :key="'old-' + idx"
                                  class="screenshot-item"
                                  @click="openPreview(screenshot.imgPath)"
                                >
                                  <img :src="screenshot.imgPath" />
                                  <div class="screenshot-overlay">
                                    <span class="material-symbols-outlined"
                                      >zoom_in</span
                                    >
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div class="version-separator">
                              <span class="material-symbols-outlined"
                                >arrow_downward</span
                              >
                            </div>

                            <div class="version-block">
                              <div class="version-label">
                                {{ $t("Apps.newVersion") }}
                              </div>
                              <div class="screenshots-scroll">
                                <div
                                  v-for="(screenshot, idx) in Object.values(
                                    field.newValue
                                  )"
                                  :key="'new-' + idx"
                                  class="screenshot-item"
                                  @error="
                                    (e) => (e.target.style.display = 'none')
                                  "
                                  @click="openPreview(screenshot.imgPath)"
                                >
                                  <img
                                    :src="screenshot.imgPath"
                                    @error="
                                      (e) => (e.target.style.display = 'none')
                                    "
                                    @click="openPreview(screenshot.imgPath)"
                                  />
                                  <div class="screenshot-overlay">
                                    <span class="material-symbols-outlined"
                                      >zoom_in</span
                                    >
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                      <div v-else class="comparison">
                        <div class="old-value">
                          <div class="label">{{ $t("Apps.pervious") }}</div>
                          <div
                            class="value"
                            :class="getFieldName(field.actionId)"
                            v-html="
                              formatValue(field.oldValue, field.actionId, 'old')
                            "
                            @click="
                              handleValueClick(
                                $event,
                                field.actionId,
                                field.oldValue
                              )
                            "
                          ></div>
                        </div>
                        <div class="arrow">
                          <span class="material-symbols-outlined"
                            >arrow_forward</span
                          >
                        </div>
                        <div class="new-value">
                          <div class="label">{{ $t("Apps.new") }}</div>
                          <div
                            class="value"
                            :class="getFieldName(field.actionId)"
                            v-html="
                              formatValue(field.newValue, field.actionId, 'new')
                            "
                            @click="
                              handleValueClick(
                                $event,
                                field.actionId,
                                field.newValue
                              )
                            "
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <NotFoundContent
            v-if="processedChanges.length === 0"
            :message="'Try adjusting your filters to see results'"
          />
        </transition-group>

        <div v-if="previewImage" class="preview-modal" @click="closePreview">
          <div class="modal-content">
            <button class="close-button" @click="closePreview">
              <span class="material-symbols-outlined">close</span>
            </button>
            <img :src="previewImage" alt="Preview" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <Modal
    :isOpen="isVersionHistoryOpen"
    :modalTitle="$t('Apps.version')"
    :actionButton="false"
    @close="closeModal"
  >
    <div class="version-history">
      <div
        v-for="version in selectedVersions"
        :key="version.versionDisplay"
        class="version-item"
      >
        <div class="version-header">
          <h3>{{ $t("Apps.version") }} {{ version.versionDisplay }}</h3>
          <span class="version-date">{{
            formatDate(version.releaseDate)
          }}</span>
        </div>
        <p class="version-notes">{{ version.releaseNotes }}</p>
        <div class="version-timestamp">
          {{ $t("Apps.released") }}:
          {{ formatDateTime(version.releaseTimestamp) }}
        </div>
      </div>
    </div>
  </Modal>

  <Modal
    :isOpen="isHistoricalView"
    :modalTitle="$t('Apps.appHistory')"
    :actionButton="false"
    @close="closeModal"
  >
    <div class="record-details">
      <app-header
        :app="appHistoryData.app"
        :platformType="appHistoryData.platformType"
      />
      <TabView
        :appData="appHistoryData"
        :historyData="null"
        :isHistory="false"
        :historyId="null"
      />
    </div>
  </Modal>
</template>

<script>
import AppActionEnum from "@/enums/AppActionEnum";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import TabView from "./TabView.vue";
import AppHeader from "./AppHeader.vue";
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import MultiSelectFilterComponent from "@/components/user/common/MultiSelectFilterComponent.vue";
import { formattedContent } from "@/ultis/user/functions/contentFunctions";

const MAX_TEXT_LENGTH = 25;
const defaultActions = AppActionEnum.getFilterOptions().map((action) => action.id);

export default {
  name: "HistoryTab",
  components: {
    Modal,
    AppHeader,
    TabView,
    NotFoundContent,
    MultiSelectFilterComponent,
  },
  props: {
    historyData: {
      type: Array,
      required: true,
    },
    historyId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      expandedItems: [],
      previewImage: null,
      isVersionHistoryOpen: false,
      isHistoricalView: false,
      selectedVersions: [],
      appHistoryData: null,
      expandedTexts: new Set(),
      showFilterDropdown: false,
      selectedFilters: [...defaultActions],
      localHistoryId: this.historyId,
      maxVisibleTags: window.innerWidth <= 768 ? 1 : 3,
    };
  },
  computed: {
    processedChanges() {
      return this.historyData
        .filter((item) => {
          if (this.selectedFilters.length === 0) return true;

          return item.app_history_actions.some((action) =>
            this.selectedFilters.includes(action.actionId)
          );
        })
        .map((item) => {
          return {
            timestamp: item.created_at,
            id: item.id,
            changedFields: item.app_history_actions.map((action) => ({
              name: this.getFieldName(action.actionId).toLowerCase(),
              actionId: action.actionId,
              appData: item.appData,
              oldValue: this.parseValue(action.oldValue, action.actionId),
              newValue: this.parseValue(action.newValue, action.actionId),
            })),
          };
        });
    },
    availableFilters() {
      return defaultActions.map((id) => ({
        id,
        label: this.getFieldName(id),
      }));
    },
    lastExpandedItem() {
      return this.expandedItems[this.expandedItems.length - 1];
    },
    actionFilters() {
      return AppActionEnum.getFilterOptions();
      return AppActionEnum.getAllOptions().sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
  },
  methods: {
    getMoreFieldsTooltip(fields) {
      return fields.map((field) => this.formatFieldName(field.name)).join("\n");
    },
    getLimitedFields(fields) {
      return fields.slice(0, this.maxVisibleTags);
    },
    handleFilterChange(filters) {
      this.selectedFilters = [...filters];
    },
    isScreenshotAction(action) {
      return action === AppActionEnum.SCREENSHOTS;
    },
    formatDate(timestamp) {
      const localeTime = localStorage.getItem("localeTime");
      return new Date(timestamp).toLocaleDateString(localeTime, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    formatFieldName(name) {
      return name
        .split(/(?=[A-Z])/)
        .join(" ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },
    formatDateTime(timestamp) {
      const localeTime = localStorage.getItem("localeTime");
      return new Date(timestamp).toLocaleString(localeTime, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getFieldName(actionId) {
      return AppActionEnum.nameMap[actionId] || "Unknown";
    },
    getFieldIcon(fieldName) {
      const icons = {
        screenshots: "photo_library",
        updatedon: "update",
        createdon: "create",
        versions: "update",
        "reviews count": "planner_review",
        ratings: "star_rate",
        downloads: "download",
        "age rating": "verified_user",
        icon: "image",
        supports: "support_agent",
        "first create": "new_releases",
        "has video": "subscriptions",
        "video link": "play_arrow",
        languages: "translate",
        compatibility: "devices",
        "what's new": "editor_choice",
        rate: "trending_up",
        name: "app_registration",
        "short description": "short_text",
        "long description": "description",
        category: "category",
        "in-app purchases": "credit_card",
        status: "published_with_changes",
      };
      return icons[fieldName.toLowerCase()] || "edit";
    },
    getFieldIconClass(fieldName) {
      return `info-icon ${fieldName.toLowerCase().replace(/[\s'"]/g, "")}`;
    },
    parseValue(value, actionId) {
      try {
        const parsed = JSON.parse(value);
        if (actionId === AppActionEnum.SCREENSHOTS) {
          return Object.values(parsed).map((screenshot) => ({
            imgPath: screenshot.imgPath,
          }));
        }
        return parsed;
      } catch {
        return value;
      }
    },
    toggleItem(index) {
      const position = this.expandedItems.indexOf(index);
      if (position === -1) {
        this.expandedItems.push(index);
      } else {
        this.expandedItems.splice(position, 1);
      }
    },
    openPreview(imgPath) {
      this.previewImage = imgPath;
      document.body.style.overflow = "hidden";
    },
    closePreview() {
      this.previewImage = null;
      document.body.style.overflow = "";
    },
    startTransition(el) {
      el.style.height = el.scrollHeight + "px";
    },
    endTransition(el) {
      el.style.height = "0px";
    },
    formatValue(value, actionId, type) {
      try {
        if (value === null) return "N/A";
        if (actionId === AppActionEnum.VERSIONS) {
          if (Array.isArray(value) && value.length > 0) {
            const displayVersion = value[0]?.versionDisplay || "N/A";
            const encodedVersions = encodeURIComponent(JSON.stringify(value));
            return `
              <div class="info-value version-display">
                <span class="version-text">${displayVersion}</span>
                <button
                  class="version-history-button-tab"
                  title="View version history"
                  data-versions="${encodedVersions}">
                  <span class="material-symbols-outlined">history</span>
                </button>
            </div>
            `;
          } else {
            return `<span class="version-text">${value || "N/A"}</span>`;
          }
        }
        if (actionId === AppActionEnum.ICON_URL) {
          if (!value) return "N/A";
          return `<img src="${value}" alt="Icon" class="clickable-image" 
        style="height: 100px; margin-right: 8px; border-radius: 10px; cursor: pointer;"
        onerror="this.onerror=null; this.src='data:image/svg+xml,<svg xmlns=&quot;http://www.w3.org/2000/svg&quot; width=&quot;100&quot; height=&quot;100&quot;><rect width=&quot;100%&quot; height=&quot;100%&quot; fill=&quot;#ccc&quot; /><text x=&quot;50%&quot; y=&quot;50%&quot; alignment-baseline=&quot;middle&quot; text-anchor=&quot;middle&quot; font-size=&quot;16&quot; fill=&quot;black&quot;>N/A</text></svg>';">`;
        }
        if (
          actionId === AppActionEnum.LONG_DESCRIPTION ||
          actionId === AppActionEnum.SHORT_DESCRIPTION
        ) {
          value = formattedContent(value);
        }
        if (actionId === AppActionEnum.STATUS){
          return value ? "Active" : "InActive";
        } 
        return value;
      } catch {
        return value;
      }
    },
    formatLongText(text, maxLength, type, uniqueIdSuffix = "") {
      if (typeof text !== "string") return text;
      if (text.length <= maxLength) return text;

      const safeEncode = (str) => {
        const encoder = new TextEncoder();
        const data = encoder.encode(str);
        return btoa(String.fromCharCode(...data));
      };

      const uniqueId = safeEncode(text).substring(0, 10) + uniqueIdSuffix;
      const isExpanded = this.expandedTexts.has(uniqueId);

      if (isExpanded) {
        return `${text} <span class="read-more-link" data-id="${uniqueId}">(Show less)</span>`;
      }

      return `${text.substring(
        0,
        maxLength
      )}... <span class="read-more-link" data-id="${uniqueId}">(Read more)</span>`;
    },
    handleValueClick(event, actionId, value) {
      if (event.target.tagName === "IMG") {
        this.openPreview(event.target.src);
      } else if (event.target.classList.contains("read-more-link")) {
        const textId = event.target.dataset.id;
        this.toggleText(textId);
      }
    },
    closeVersionsModal() {
      this.isVersionHistoryOpen = false;
      this.selectedVersions = [];
    },
    toggleVersionsModal(versions) {
      this.selectedVersions = versions;
      this.isVersionHistoryOpen = true;
    },
    toggleText(uniqueId) {
      if (this.expandedTexts.has(uniqueId)) {
        this.expandedTexts.delete(uniqueId);
      } else {
        this.expandedTexts.add(uniqueId);
      }
      this.$forceUpdate();
    },
    toggleHistoricalView(data) {
      const historyId = data?.id;
      if (!historyId) return;
      const routeData = this.$router.resolve({
        name: "History",
        params: { historyId },
      });
      window.open(routeData.href, "_blank");
    },
    openHistoricalView() {
      this.isHistoricalView = true;
    },
    closeModal() {
      this.selectedVersions = [];
      this.isVersionHistoryOpen = false;
      this.isHistoricalView = false;
      this.appHistoryData = null;
    },
    handleClickOutside(event) {
      if (
        this.$refs.filterDropdown &&
        !this.$refs.filterDropdown.contains(event.target)
      ) {
        this.showFilterDropdown = false;
      }
    },
    scrollToHistoryItem() {
      if (this.localHistoryId) {
        const targetIndex = this.processedChanges.findIndex(
          (change) => change.id == this.historyId
        );

        if (targetIndex !== -1) {
          if (!this.expandedItems.includes(targetIndex)) {
            this.expandedItems.push(targetIndex);
          }
        }
      }
      this.localHistoryId = null;
    },
    scrollToItem(index) {
      this.$nextTick(() => {
        let targetElement = this.$refs["timelineItem" + index];

        if (targetElement[0]) {
          let targetPosition = targetElement[0].offsetTop;
          window.scrollTo({
            top: targetPosition - window.innerHeight * 0.25,
            behavior: "smooth",
          });
        } else {
          console.error(
            "Target element not found or scrollIntoView not available"
          );
        }
      });
    },
  },
  updated() {
    window.removeEventListener("resize", this.updateTagVisibility);
    this.scrollToHistoryItem();
    this.lastExpandedItem = null;
  },
  mounted() {
    this.$nextTick(() => {
      document.addEventListener("click", (event) => {
        if (event.target.closest(".version-history-button-tab")) {
          const button = event.target.closest(".version-history-button-tab");
          const versions = JSON.parse(
            decodeURIComponent(button.dataset.versions)
          );
          this.toggleVersionsModal(versions);
        }
      });
    });
    window.addEventListener("resize", this.updateTagVisibility);
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    lastExpandedItem(newIndex) {
      if (newIndex !== undefined) {
        this.scrollToItem(newIndex);
      }
    },
  },
};
</script>

<style scoped>
.action-tags {
  display: flex;
  gap: 8px;
}

.action-tag {
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  user-select: none;
  white-space: nowrap;
}

.action-more {
  font-size: 14px;
  font-weight: bold;
  color: #5f6368;
}
/** Filter */
.filter-dropdown {
  position: relative;
  max-width: 400px;
}

.selected-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #dfe1e5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.selected-filters:hover {
  border-color: #1a73e8;
}

.filter-label {
  color: #5f6368;
  font-size: 14px;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex-grow: 1;
  font-size: 12px;
  border-radius: 7px;
}

.filter-chip {
  background: #e8f0fe;
  color: #1a73e8;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.remove-filter {
  display: flex;
  cursor: pointer;
  opacity: 0.7;
}

.remove-filter span {
  font-size: 14px;
}

.remove-filter:hover {
  opacity: 1;
}

.dropdown-arrow {
  margin-left: auto;
  transition: transform 0.2s;
}

.filter-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dfe1e5;
  border-radius: 8px;
  margin-top: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}

.filter-option {
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background 0.2s;
}

.filter-option:hover {
  background: #f8f9fa;
}

.check-icon {
  color: #5f6368;
}

.check-icon.selected {
  color: #1a73e8;
}

.slide-fade-enter-active {
  transition: all 0.2s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.15s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/** Histroy Tab */
.date {
  font-size: 16px;
  font-weight: 500;
  color: #1a73e8;
}

.change-item {
  margin-top: 16px;
}

.change-item:first-child {
  margin-top: 0;
}

.screenshots-section h3 {
  font-size: 16px;
  font-weight: 500;
  color: #202124;
  margin: 0 0 16px 0;
}

.screenshots-comparison {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.version-block {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.version-label {
  font-size: 14px;
  font-weight: 500;
  color: #5f6368;
  margin-bottom: 12px;
}

.version-separator {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #5f6368;
  height: 24px;
}

.screenshots-scroll {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #888 #f1f1f1;
}

.screenshots-scroll::-webkit-scrollbar {
  height: 6px;
}

.screenshots-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.screenshots-scroll::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.screenshot-item {
  flex: 0 0 80px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease;
  /* aspect-ratio: 9/16; */
}

.screenshot-item:hover {
  transform: translateY(-2px);
}

.screenshot-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.screenshot-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.screenshot-overlay .material-symbols-outlined {
  color: white;
  font-size: 24px;
}

.screenshot-item:hover .screenshot-overlay {
  opacity: 1;
}

.change-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
}

.change-type {
  font-size: 14px;
  color: #5f6368;
  margin-bottom: 8px;
}

.change-value {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
}

.history-tab {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.clickable-image {
  cursor: pointer;
  transition: opacity 0.2s;
}

.clickable-image:hover {
  opacity: 0.8;
}

.content-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 30px;
  margin-bottom: 32px;
}

.title-section h2 {
  font-size: 24px;
  color: #202124;
  margin: 0;
}

.timeline {
  margin-top: 24px;
}

.timeline-item {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}

.timeline-date {
  min-width: 150px;
  padding-top: 16px;
  text-align: right;
}

.date {
  font-size: 14px;
  color: #666;
}

.timeline-content {
  flex: 1;
  background: #f8f9fa;
  border-radius: 12px;
  overflow: hidden;
}

.change-header {
  padding: 16px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.change-summary-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.change-type {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  background: #e8f0fe;
  color: #1a73e8;
}

.change-summary {
  font-size: 14px;
  color: #5f6368;
  user-select: none;
  white-space: nowrap;
}

.expanded {
  box-shadow: 0 0 5px #9e9e9e;
}

.expand-arrow {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  box-shadow: none;
}

.expand-arrow.expanded {
  transform: rotate(180deg);
}

.changes-content {
  border-top: 1px solid #e8eaed;
  padding: 16px;
  overflow: hidden;
  transition: height 0.3s ease;
}

.changes-grid {
  display: grid;
  gap: 16px;
}

.change-field {
  background: white;
  border-radius: 8px;
  padding: 16px;
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.field-name {
  font-size: 14px;
  color: #202124;
  text-transform: capitalize;
}

.field-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.field-icon .material-symbols-outlined {
  font-size: 20px;
}

.field-versions {
  background: #f3e5f5;
  color: #9c27b0;
}
.field-size {
  background: #e8f0fe;
  color: #1a73e8;
}

.history-tab-container {
  width: 100%;
}

.field-screenshots {
  background: #e3f2fd;
  color: #1e88e5;
}

.field-updatedon {
  background: #e8f5e9;
  color: #43a047;
}

.field-inapppurchase {
  background: #fce4ec;
  color: #e91e63;
}
.field-description {
  background: #e8f5e9;
  color: #4caf50;
}

.comparison {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  /* align-items: center; */
}

.old-value,
.new-value {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
}

.label {
  font-size: 12px;
  color: #5f6368;
  margin-bottom: 8px;
}

.value {
  font-size: 14px;
  font-weight: 500;
  color: #202124;
  word-break: break-word;
}

.arrow {
  color: #5f6368;
  display: flex;
  justify-content: center;
}

.preview-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.modal-content img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 10px;
}

.close-button {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
}

.close-button .material-symbols-outlined {
  font-size: 32px;
}

/* Timeline animations */
.timeline-enter-active,
.timeline-leave-active {
  transition: all 0.5s ease;
}

.timeline-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.timeline-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

/* Expand content animation */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  height: 0 !important;
  opacity: 0;
}
.record-details {
  max-height: 70vh;
  overflow-y: auto;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.record-details pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .timeline-item {
    display: block;
  }
  .timeline-date {
    text-align: left;
    padding-top: 0;
    padding-bottom: 5px;
  }
  .action-tags {
    display: flex;
    gap: 4px;
  }
  .action-tag {
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 8px;
    color: white;
  }
  .action-more {
    font-size: 14px;
    font-weight: bold;
    color: #5f6368;
  }
  .header {
    gap: 10px;
    flex-wrap: wrap;
    padding-left: 0px ;
  }
}
</style>
